import { Archive, Copy, Flash, Play, VideoOctagon } from "iconsax-reactjs"
import { Check } from "lucide-react"
import { GoVerified } from "react-icons/go"

import { TabTag } from "@/components/shared"
import { Button, Image } from "@/components/ui"
import { CommissionRateBanner } from "@/features/jobs/explore/components/campaign/campaign-ui"
import { EAffiliateOrderType, EFolderAssets } from "@/modules/helpers/enum"
import { useCampaignActions, useMultipleCopyToClipboard } from "@/modules/helpers/hooks"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import { getImageByNameFromS3, sendOpenLink } from "@/modules/helpers/utils"
import type { AffiliateCampaign } from "@/modules/types"

// Component để detect và render links trong text
const LinkifiedText = ({ text }: { text?: string }) => {
  if (!text) return null

  // Regex để detect URLs (http, https, www)
  const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/g
  const parts = text.split(urlRegex)

  return (
    <>
      {parts.map((part, index) => {
        if (!part) return null

        // Check nếu part là URL
        const isUrl = /^(https?:\/\/[^\s]+)|(www\.[^\s]+)$/.test(part)

        if (isUrl) {
          const url = part.startsWith("www.") ? `https://${part}` : part
          return (
            <span
              key={index}
              onClick={(e) => {
                e.stopPropagation()
                sendOpenLink(url)
              }}
              className="text-[#4674BA] break-all underline cursor-pointer hover:text-blue-200 transition-colors"
            >
              {part}
            </span>
          )
        }

        return <span key={index}>{part}</span>
      })}
    </>
  )
}

interface CampaignManageDetailProps {
  data?: AffiliateCampaign
  isShowStatus?: boolean
  layoutBanner?: "upper" | "after"
  status?: React.ReactNode
}

export const AffiliateCampaignDetail = ({
  data,
  isShowStatus = false,
  layoutBanner = "after",
  status
}: CampaignManageDetailProps) => {
  const { copiedStates, copy } = useMultipleCopyToClipboard(["link", "hashtag"] as const)
  const { handleCopyLink, handleCopyHashtag } = useCampaignActions({
    productLink: data?.products?.link,
    hashtag: data?.hashtag
  })

  const copyToClipboard = () => {
    if (data?.products?.link) {
      copy("link", data.products.link)
      handleCopyLink()
    }
  }

  const copyHashTag = () => {
    const hashtag = data?.hashtag?.join(" ")
    if (hashtag) {
      copy("hashtag", hashtag)
      handleCopyHashtag()
    }
  }

  return (
    <>
      <div className="space-y-2.5 p-3 bg-white z-10 relative">
        {/* Thumbnail */}
        <div className="w-full rounded-2xl aspect-square border border-gray-200 overflow-hidden relative">
          {data?.thumbnailName && (
            <Image
              src={
                data?.thumbnailName?.includes("https") || data.thumbnailName?.includes("base64")
                  ? data?.thumbnailName
                  : getImageByNameFromS3(
                      data.thumbnailName,
                      data?.createdBy,
                      EFolderAssets.THUMBNAIL_AFFILIATE_CAMPAIGN
                    )
              } // adjust to your getImageByNameFromS3
              alt="thumbnail"
              className="w-full aspect-square rounded-lg shadow-sm group-hover:scale-110 object-cover transition-all duration-200 ease-linear"
            />
          )}
          <div className="rounded-tr-[16px] rounded-bl-[10px] border-l-[0.5px] border-b-[0.5px] border-neutral-200 bg-gradient-primary absolute bottom-0  px-4 flex justify-center items-center gap-1 py-1.5 text-white">
            <GoVerified size={16} color="#fff" />
            <span className="text-xs">{data?.shopName || "Nhãn hàng"}</span>
          </div>
        </div>
        {/* Title */}
        {!!(data as AffiliateCampaign)?.cashAmount && (
          <span className="text-gradient-primary text-base font-bold py-1">
            Cast: {Number((data as AffiliateCampaign)?.cashAmount)?.toLocaleString()}đ /KOC
          </span>
        )}
        <h2 className="font-semibold text-base">{data?.title}</h2>
        {/* <div className="flex items-center gap-1">
          <p className="text-sm font-medium text-gray-500">Hoàn thành chiến dịch</p>{" "}
          <p className="text-sm font-semibold text-gradient-primary">Nhận ngay 300.000</p>{" "}
          <UsdCoin size={18} color="#045664" />
        </div> */}
        <div className="flex gap-1">
          {/* <TabTag label="Zxu" isTextGradient /> */}
          {!!(data as AffiliateCampaign)?.cashAmount && <TabTag label="Cast camp" isTextGradient />}
          {(data as AffiliateCampaign)?.sendVia === EAffiliateOrderType.SEND_OUTSIDE && (
            <TabTag label="Gửi ngoài sàn TikTok" />
          )}
          {(data as AffiliateCampaign)?.sendVia === EAffiliateOrderType.NORMAL && (
            <TabTag label="Gửi trong sàn TikTok" />
          )}
          <TabTag label="Mẫu miễn phí" />
          {!!data?.isSelfFaceRequired && <TabTag label="Cần show mặt" />}
        </div>
        {isShowStatus && status}
        {!!data?.hashtag?.length && (
          <div className="flex flex-col gap-1 py-4 px-4 rounded-2xl bg-[#FFE8D2] transition-transform text-white">
            <div className="flex items-center gap-1">
              <Flash size="16" color="#E46A1F" variant="Outline" />
              <span className="text-sm font-semibold text-[#E46A1F]">Hashtag bắt buộc</span>
            </div>
            <div className="flex gap-1 items-center">
              <div className="flex gap-1 flex-wrap flex-1">
                {data?.hashtag?.map((item, index) => (
                  <span key={index} className="text-xss tiny:text-xs text-[#CC5A0F] font-light">
                    {item}
                  </span>
                ))}
              </div>
              <Button
                size="icon"
                variant="ghost"
                className={cn("h-6 w-6", {
                  "bg-white": copiedStates.hashtag
                })}
                onClick={copyHashTag}
              >
                {copiedStates.hashtag ? (
                  <Check className="h-4 w-4 text-[#CC5A0F]" />
                ) : (
                  <Copy size="12" color="#CC5A0F" />
                )}
              </Button>
            </div>
          </div>
        )}
        {/* Status */}

        {/* Description */}
        <div className="flex flex-col gap-2 py-4 px-4 rounded-2xl bg-[#EAF3FF] transition-transform text-[#4674BA]">
          <div className="flex gap-1 items-center">
            <Archive size="16" color="#4674BA" variant="Outline" />
            <span className="text-sm font-semibold">Mô tả chiến dịch</span>
          </div>
          <p className="text-xss tiny:text-xs font-light break-words whitespace-pre-line">
            <LinkifiedText text={data?.description} />
          </p>
          <div className="flex gap-1 items-center text-white">
            <div
              onClick={() => {
                if (data?.products?.link) {
                  sendOpenLink(data.products.link)
                }
              }}
              className="font-normal truncate underline text-xs text-[#4674BA] w-full overflow-ellipsis"
            >
              {data?.products?.link}
            </div>
            <Button
              size="icon"
              variant="ghost"
              className={cn("h-6 w-6", {
                "bg-white": copiedStates.link
              })}
              onClick={copyToClipboard}
            >
              {copiedStates.link ? (
                <Check className="h-4 w-4 text-[#4674BA]" />
              ) : (
                <Copy size="12" color="#4674BA" />
              )}
            </Button>
          </div>
        </div>

        {/* {!!data?.potentials?.length && (
          <div className="space-y-3">
            {data.potentials.map((item, idx) => (
              <PostPreviewCard key={idx} data={item} title={"Tiktok"} icon={<TikTokIcon />} />
            ))}
          </div>
        )} */}
        {/* Videos */}
        {layoutBanner === "upper" && data?.products?.creatorCommissionRate && (
          <div className="mb-4">
            <CommissionRateBanner
              commissionRate={data?.products?.creatorCommissionRate}
              maxCommission={data?.products?.creatorCommissionValue}
            />
          </div>
        )}
        {!!data?.clipSimpleUrl?.length && (
          <div className="flex flex-col gap-2 py-3 rounded-xl transition-transform text-black">
            <p className="text-xs font-medium flex items-center gap-1">
              <VideoOctagon size="16" color="#000" variant="Outline" />{" "}
              <span className="text-sm font-semibold">Video tham khảo</span>
            </p>
            <div className="flex gap-3">
              {data?.clipSimpleUrl?.map((item, index) => (
                <div
                  key={index}
                  onClick={() => {
                    sendOpenLink(item)
                  }}
                  className="relative w-[97px] h-[150px] bg-black rounded-xl flex items-center justify-center"
                >
                  <div className="bg-gradient-primary rounded-xl w-5 h-5 flex items-center justify-center">
                    <Play size="12" color="#fff" variant="Bold" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Potential Posts */}
      </div>
    </>
  )
}
